import Decimal from 'decimal.js';

import { STATE_CODE_TO_NAME } from '@/constants/states';
import { useUnguardedHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AfterDeath, StateCode } from '@/types/schema';

import { TaxDetail_StateTaxDetailFragment } from './graphql/TaxDetail.generated';

/**
 * Consolidated label used when more than the per-tile budget of state tax
 * lines would otherwise be itemized.
 */
export const CONSOLIDATED_NON_RESIDENT_LABEL = 'Non-resident estate taxes';

export const APPORTIONMENT_RATIO_TOOLTIP =
  'Ratio of non-resident real and tangible personal property to total estate value. Used to calculate the portion of estate tax owed to the non-resident state.';

/**
 * Per the Notion spec linked in the PM doc, summary/diagram tiles itemize
 * up to 4 tax lines; beyond that, non-residential states collapse into a
 * single "Non-resident taxes" line.
 */
export const TAX_TILE_ITEMIZATION_LIMIT = 4;

export interface StateTaxLine {
  key: string;
  label: string;
  tax: Decimal;
  /** Null on the consolidated "Non-resident taxes" line. */
  stateCode: StateCode | null;
  isConsolidated: boolean;
}

/**
 * Sorts state estate-tax entries residential-first, then non-residential
 * alphabetically, dropping zero-tax states. When `otherTaxLineCount` is given
 * and the per-tile budget would be exceeded, non-residential states collapse
 * into a single "Non-resident taxes" line.
 *
 * Used by:
 * - the diagram tile chips (`useTaxBreakout`, single residential state)
 * - the final-summary tile preview rows (both grantors' states are residential)
 * - the side-panel itemized listing (no consolidation; pass undefined for
 *   `otherTaxLineCount`).
 */
export function buildStateTaxLines({
  stateTaxByCode,
  residentialStateCodes,
  otherTaxLineCount,
  formatStateLabel = (stateCode) => `${stateCode} estate tax`,
}: {
  stateTaxByCode: Record<StateCode, Decimal>;
  residentialStateCodes: ReadonlySet<StateCode>;
  /** Federal + GST + each state inheritance tax line on the same tile. Omit to skip consolidation. */
  otherTaxLineCount?: number;
  formatStateLabel?: (stateCode: StateCode) => string;
}): StateTaxLine[] {
  const sorted = (Object.entries(stateTaxByCode) as [StateCode, Decimal][])
    .filter(([, tax]) => tax.greaterThan(0))
    .sort(([a], [b]) => {
      const aIsResidential = residentialStateCodes.has(a);
      const bIsResidential = residentialStateCodes.has(b);
      if (aIsResidential && !bIsResidential) return -1;
      if (!aIsResidential && bIsResidential) return 1;
      return a.localeCompare(b);
    });

  const lines: StateTaxLine[] = sorted.map(([stateCode, tax]) => ({
    key: stateCode,
    label: formatStateLabel(stateCode),
    tax,
    stateCode,
    isConsolidated: false,
  }));

  if (otherTaxLineCount === undefined) return lines;
  // Without a residential state we can't classify "non-resident", so skip consolidation.
  if (residentialStateCodes.size === 0) return lines;
  if (lines.length + otherTaxLineCount <= TAX_TILE_ITEMIZATION_LIMIT) {
    return lines;
  }

  const residential = lines.filter(
    (l) => l.stateCode !== null && residentialStateCodes.has(l.stateCode)
  );
  const nonResidential = lines.filter(
    (l) => l.stateCode === null || !residentialStateCodes.has(l.stateCode)
  );
  // Folding a single state into "Non-resident taxes" loses info without saving a row.
  if (nonResidential.length <= 1) return lines;

  const consolidatedTotal = nonResidential.reduce(
    (acc, l) => acc.plus(l.tax),
    new Decimal(0)
  );

  return [
    ...residential,
    {
      key: 'non-resident-consolidated',
      label: CONSOLIDATED_NON_RESIDENT_LABEL,
      tax: consolidatedTotal,
      stateCode: null,
      isConsolidated: true,
    },
  ];
}

/**
 * State estate-tax methods used to compute taxes on real or tangible personal
 * property held in a state where the decedent was NOT a resident.
 *
 * - `apportionment`: tentative tax × apportionment ratio (CT, DC, HI, IL, ME, MD, MN, OR, RI, VT, WA)
 * - `directExclusion`: only the in-state portion of the gross estate is taxed (MA, NY)
 */
export type NonResidentialMethod = 'apportionment' | 'directExclusion';

export const APPORTIONMENT_STATES: StateCode[] = [
  StateCode.Ct,
  StateCode.Dc,
  StateCode.Hi,
  StateCode.Il,
  StateCode.Me,
  StateCode.Md,
  StateCode.Mn,
  StateCode.Or,
  StateCode.Ri,
  StateCode.Vt,
  StateCode.Wa,
];

export const DIRECT_EXCLUSION_STATES: StateCode[] = [
  StateCode.Ma,
  StateCode.Ny,
];

/**
 * State-specific override copy applied to the State-tax-due tooltip in place
 * of the generic apportionment / direct-exclusion text. HI residents receive
 * a credit for the lesser of (a) other state's tax on real/tangible personal
 * property, or (b) HI's tax attributable to that same property; HI
 * non-residents are not eligible.
 */
export const HAWAII_TAX_TOOLTIP =
  "We assume HI residents receive a credit for the lesser of (a) other state's tax on real/tangible personal property, or (b) HI's tax attributable to that same property. HI non-residents are not eligible for the credit.";

export function getNonResidentialMethod(
  stateCode: StateCode | null | undefined
): NonResidentialMethod | null {
  if (!stateCode) return null;
  if (APPORTIONMENT_STATES.includes(stateCode)) return 'apportionment';
  if (DIRECT_EXCLUSION_STATES.includes(stateCode)) return 'directExclusion';
  return null;
}

export function getNonResidentialMethodTooltip(
  method: NonResidentialMethod
): string {
  switch (method) {
    case 'apportionment':
      return 'Assumes calculations are based on fractional apportionment method for calculating taxes on real and tangible personal property held in non-resident states.';
    case 'directExclusion':
      return 'Assumes calculations are based on direct exclusion method for calculating taxes on real and tangible personal property held in non-resident states.';
  }
}

export function isNonResidentialStateTax(
  detail: Pick<TaxDetail_StateTaxDetailFragment, 'stateCode'>,
  residentialStateCode: StateCode | null | undefined
): boolean {
  if (!detail.stateCode) return false;
  if (!residentialStateCode) return false;
  return detail.stateCode !== residentialStateCode;
}

/**
 * Drops non-residential state-tax entries whose tax resolves to $0 — those
 * surfaces (side panel, tax overview) shouldn't render an empty section just
 * because the BE returned a row for an asset's situs state. Residential
 * always passes through. No-ops when residential is unknown.
 */
export function dropZeroNonResidentialStateTaxes<
  T extends Pick<TaxDetail_StateTaxDetailFragment, 'stateCode' | 'tax'>,
>(stateTaxes: T[], residentialStateCode: StateCode | null | undefined): T[] {
  if (!residentialStateCode) return stateTaxes;
  return stateTaxes.filter(
    (s) => s.stateCode === residentialStateCode || s.tax.greaterThan(0)
  );
}

/**
 * Returns the input list reordered to: residential state first, then
 * non-residential states alphabetically by state code.
 */
export function sortStateTaxes<
  T extends Pick<TaxDetail_StateTaxDetailFragment, 'stateCode'>,
>(stateTaxes: T[], residentialStateCode: StateCode | null | undefined): T[] {
  const sorted = [...stateTaxes].sort((a, b) => {
    const aIsResidential = a.stateCode === residentialStateCode;
    const bIsResidential = b.stateCode === residentialStateCode;
    if (aIsResidential && !bIsResidential) return -1;
    if (!aIsResidential && bIsResidential) return 1;
    return (a.stateCode ?? '').localeCompare(b.stateCode ?? '');
  });
  return sorted;
}

/**
 * Long label used on side-panel calculations and tax-overview rows.
 * e.g. "Washington non-resident estate tax".
 */
export function getNonResidentialLongLabel(
  stateCode: StateCode | null | undefined
): string {
  const stateName = stateCode ? STATE_CODE_TO_NAME[stateCode] : null;
  return `${stateName ?? stateCode ?? 'State'} non-resident estate tax`;
}

/**
 * Returns the residential state for a given death:
 *   - First death  → the dying grantor's state of domicile
 *   - Second death → the surviving grantor's state of domicile
 *
 * Falls back to undefined when no household is in context (e.g. compare modal,
 * presentation rendering) or when the relevant client lacks an address.
 */
export function useResidentialStateForDeath({
  afterDeath,
  firstGrantorDeathId,
}: {
  afterDeath: AfterDeath;
  firstGrantorDeathId: string | null | undefined;
}): StateCode | undefined {
  const ctx = useUnguardedHouseholdDetailsContext();
  const clients = ctx.primaryClients ?? [];
  if (clients.length === 0) return undefined;

  if (afterDeath === AfterDeath.None) {
    // Lifetime gift surfaces don't surface non-residential state estate tax,
    // but return the primary client's state for any callers that ask anyway.
    return clients.find((c) => c.isPrimary)?.stateCode ?? clients[0]?.stateCode;
  }

  const dyingClient =
    afterDeath === AfterDeath.First
      ? clients.find((c) => c.id === firstGrantorDeathId)
      : clients.find((c) => c.id !== firstGrantorDeathId);

  return dyingClient?.stateCode;
}
